// BRK-S038 — the pure mapping from a document-intake PROPOSAL to the SAME flow
// surfaces the manual path uses. This is where the single-capture-surface
// invariant is realized: a proposal never spawns a parallel document form — it
// only produces initial values for step 1, the shared AccountForm, and the
// manifest details form, plus the list of extracted keys each step must MARK for
// the broker to confirm or edit.
//
// Everything here is pure (proposal in → values + marked-key lists out) so it's
// exhaustively unit-testable (NN #1) and carries no React/fetch dependency.
import type { IntakeProposal } from "@/lib/brokerDocuments";
import type { AccountRecord, Address } from "@/lib/brokerAccounts";
import { normalizeUsPhone, type PhoneDetail } from "@/lib/phone";
import { normalizeRoles } from "@/lib/personnelRoles";
import type { CodeDisplayRef } from "@/lib/submissions/brokerSubmissions";
import type { ManifestField, ProductManifest } from "@/lib/brokerProducts";

/** A class code extraction read off a document but couldn't resolve to a
 *  canonical code (the row carries a raw `classCode` string but no resolved
 *  `codeId`). Derived from the proposed WC exposure rows — the contract does NOT
 *  ship a separate unmatched-codes list; the broker picks the match in the
 *  exposures typeahead. `path` points at the row (e.g. `wc.exposures[1]`). */
export interface UnmatchedCode {
  raw: string;
  description: string | null;
  path: string;
}

/** A trimmed non-empty string, or null. */
function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** The strings out of an unknown array value (anything else → none). */
function asStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

/** Read `{street,city,state,zip}` out of an extracted address bag, or null. */
function asAddress(value: unknown): Address | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const street = str(v.street);
  const city = str(v.city);
  const state = str(v.state);
  const zip = str(v.zip);
  if (!street && !city && !state && !zip) return null;
  return {
    id: "",
    street: street ?? "",
    city: city ?? "",
    state: state ?? "",
    zip: zip ?? "",
    formattedAddress: null,
  };
}

/**
 * Extracted phone values → the array shape the contract now uses (PMS-S173).
 *
 * An ACORD form has one printed phone box per party, so extraction proposes a
 * single `primaryPhone` / `phone` string — but it may also hand back a list, and
 * a document that supplies several numbers must keep all of them rather than
 * collapse to the first. Reads either shape.
 *
 * Unusable values are KEPT, not dropped: extraction reports what is printed, and
 * a garbled or non-US number the broker can see and correct beats one that
 * silently vanished (the form marks it for review and PhoneListField shows the
 * rejection reason). Such a row carries an empty `e164` — nothing to canonicalize.
 */
function asPhones(value: unknown, idPrefix: string): PhoneDetail[] {
  const raw = (Array.isArray(value) ? value : [value])
    .map((v) => str(v))
    .filter((v): v is string => Boolean(v));
  return raw.map((number, i) => {
    const normalized = normalizeUsPhone(number);
    return {
      id: `${idPrefix}-${i}`,
      number,
      e164: normalized.ok ? normalized.e164 : "",
      lineType: null,
      extension: normalized.ok ? (normalized.extension ?? null) : null,
      // Primary-first, exactly one — PMS rejects a body with two.
      isPrimary: i === 0,
    };
  });
}

/** The first resolvable state from the proposed account (mailing, then legal). */
function proposedState(account: Record<string, unknown>): string | null {
  return (
    asAddress(account.mailingAddress)?.state ||
    asAddress(account.legalAddress)?.state ||
    str(account.state) ||
    null
  );
}

/** Step-1 prefill (legal name + state). The broker still chooses industry +
 *  product — extraction proposes the insured, not the coverage. Empty strings
 *  when extraction found nothing, so the fields stay editable/required. */
export function proposalToStep1(proposal: IntakeProposal): {
  legalName: string;
  state: string;
  extractedKeys: string[];
} {
  const account = proposal.proposed.account ?? {};
  const legalName = str(account.legalName);
  const state = proposedState(account);
  const extractedKeys: string[] = [];
  if (legalName) extractedKeys.push("legalName");
  if (state) extractedKeys.push("state");
  return { legalName: legalName ?? "", state: state ?? "", extractedKeys };
}

/**
 * Overlay the proposed account fields onto the created draft record, yielding a
 * valid AccountRecord seed for the shared AccountForm plus the list of account
 * field keys extraction filled (so the form MARKS them). The keys match the
 * AccountForm field keys, so the markers land beside the right inputs.
 */
export function proposalToAccountSeed(
  proposal: IntakeProposal,
  draft: AccountRecord,
): { seed: AccountRecord; extractedKeys: string[] } {
  const account = proposal.proposed.account ?? {};
  const extractedKeys: string[] = [];
  const seed: AccountRecord = { ...draft };

  /**
   * Copy one proposed value onto the seed and mark the form field it landed on.
   *
   * `proposalKeys` is a LIST because the proposal's `account` is an open map on
   * the wire (PMS-S131) rather than a closed shape — so when the account
   * contract renamed `url` to `website` (PMS-S314) there was nothing in the
   * spec to say which spelling extraction emits. Accepting either is free
   * there, and the MARKER key stays the form's own so it lands beside the right
   * input.
   */
  const setStr = (
    key: keyof AccountRecord,
    markerKey: string,
    proposalKeys: readonly string[] = [markerKey],
  ) => {
    for (const proposalKey of proposalKeys) {
      const value = str(account[proposalKey]);
      if (value) {
        (seed[key] as string) = value;
        extractedKeys.push(markerKey);
        return;
      }
    }
  };
  // The draft already carries the confirmed legal name from step 1; only mark it
  // when extraction is the source (it always is on the document path).
  setStr("legalName", "legalName");
  setStr("dbaName", "dbaName");
  setStr("emailAddress", "emailAddress");

  setStr("ein", "ein");
  setStr("website", "website", ["website", "url"]);

  // Either spelling: the array the contract now uses, or the single printed
  // number extraction reads off the form.
  const phones = asPhones(
    account.phones ?? account.primaryPhone,
    "extracted-phone",
  );
  if (phones.length) {
    seed.phones = phones;
    extractedKeys.push("phones");
  }

  const entityType = str(account.entityType);
  if (entityType) {
    seed.entityType = entityType as AccountRecord["entityType"];
    extractedKeys.push("entityType");
  }

  const legal =
    asAddress(account.legalAddress) ?? asAddress(account.mailingAddress);
  if (legal) {
    seed.legalAddress = legal;
    extractedKeys.push("legalAddress");
  }
  const mailing = asAddress(account.mailingAddress);
  if (mailing) seed.mailingAddress = mailing;

  const people = Array.isArray(account.personnel) ? account.personnel : [];
  const personnel = people
    .map((p) =>
      p && typeof p === "object" ? (p as Record<string, unknown>) : {},
    )
    .filter((p) => str(p.firstName) || str(p.lastName))
    .map((p, i) => ({
      id: `extracted-${i}`,
      firstName: str(p.firstName) ?? "",
      lastName: str(p.lastName) ?? "",
      title: str(p.title),
      email: str(p.email),
      phones: asPhones(p.phones ?? p.phone, `extracted-phone-${i}`),
      ownershipPercentage: null,
      // The ACORD role contacts (inspection / accounting / claims) arrive as
      // personnel carrying a role (PMS-S179), so carry the roles through to the
      // form rather than dropping what extraction resolved. Normalized because a
      // duplicate would 400 on save.
      roles: normalizeRoles(asStrings(p.roles)),
      // Nothing is ON FILE for a person extraction just proposed — they have no
      // stored row yet, so no ciphertext and no last four (BRK-S105). An SSN
      // printed on the uploaded ACORD is encrypted into the document's own
      // extraction record and never surfaced (PMS docs/security.md), so this is
      // the only honest value: not on file, from this portal's point of view.
      // `usedForWrite` is likewise false: the server names the person a write
      // reads from (PMS-S238), and it cannot have named somebody who has no
      // stored row yet. Proposing `true` here would be this portal inventing
      // the very answer the flag exists to stop it inventing.
      pii: {
        ssn: { onFile: false, last4: null },
        driversLicense: { onFile: false },
        usedForWrite: false,
      },
    }));
  if (personnel.length) {
    seed.personnel = personnel;
    extractedKeys.push("personnel");
  }

  return { seed, extractedKeys };
}

/**
 * The proposed submission field bag for a product, narrowed to that product
 * manifest's SUBMISSION field keys (account-scope + unknown keys are dropped —
 * the account fields land on the account, and the server owns unknown keys). The
 * side is chosen by line of business (wc / gl / commercial_auto has none yet).
 * Term dates are never proposed — a fresh effective date is always entered — so
 * the effective-date field stays blank + required, exactly like a renewal.
 * Returns the bag, the keys extraction filled (to MARK), and the unmatched class
 * codes for that side (to surface a resolution affordance).
 */
export function proposalToSubmissionBag(
  proposal: IntakeProposal,
  manifest: ProductManifest,
): {
  bag: Record<string, unknown>;
  extractedKeys: string[];
  unmatchedCodes: UnmatchedCode[];
  codeRefs: Record<string, CodeDisplayRef>;
} {
  const sideKey =
    manifest.lineOfBusiness === "workers_compensation"
      ? "wc"
      : manifest.lineOfBusiness === "general_liability"
        ? "gl"
        : null;
  const source = (sideKey ? proposal.proposed[sideKey] : {}) ?? {};
  const submissionFields = manifest.fields.filter(
    (f) => f.scope === "submission",
  );

  const bag: Record<string, unknown> = {};
  const extractedKeys: string[] = [];
  const unmatchedCodes: UnmatchedCode[] = [];
  const codeRefs: Record<string, CodeDisplayRef> = {};

  for (const field of submissionFields) {
    // Never propose the term's effective date — always entered fresh.
    if (field.key === "effectiveDate") continue;
    const value = source[field.key];
    if (value === undefined || value === null) continue;

    if (field.type === "group" && Array.isArray(value)) {
      // Narrow each extracted row to the group's declared item keys (dropping
      // extractor extras like the raw `classCode` sibling), and, for a classCode
      // item, derive the matched code refs + the unmatched rows to resolve.
      const rows = narrowGroupRows(
        value as Record<string, unknown>[],
        field,
        `${sideKey}.${field.key}`,
        codeRefs,
        unmatchedCodes,
      );
      if (rows.length) {
        bag[field.key] = rows;
        extractedKeys.push(field.key);
      }
      continue;
    }

    bag[field.key] = value;
    extractedKeys.push(field.key);
  }

  return { bag, extractedKeys, unmatchedCodes, codeRefs };
}

// The extractor emits a class-code row as `{ classCode: "<raw>", codeId?: "<id>" }`
// (PMS-S131): `codeId` is the resolved canonical id (present only when matched),
// `classCode` is the raw string off the form. This narrows each row to the group's
// declared item keys AND, for the classCode item, records the display ref (matched)
// or the unmatched entry (raw code, no resolved id).
const RAW_CODE_KEY = "classCode";
function narrowGroupRows(
  rows: Record<string, unknown>[],
  field: ManifestField,
  pathBase: string,
  codeRefs: Record<string, CodeDisplayRef>,
  unmatched: UnmatchedCode[],
): Record<string, unknown>[] {
  const items = field.item ?? [];
  const itemKeys = items.map((i) => i.key);
  const codeItem = items.find((i) => i.type === "classCode");
  return rows.map((row, index) => {
    const narrowed: Record<string, unknown> = {};
    for (const key of itemKeys) {
      if (row[key] !== undefined && row[key] !== null) narrowed[key] = row[key];
    }
    if (codeItem && codeItem.key !== RAW_CODE_KEY) {
      const raw = row[RAW_CODE_KEY];
      const resolvedId = narrowed[codeItem.key];
      if (typeof resolvedId === "string" && typeof raw === "string") {
        codeRefs[resolvedId] = { id: resolvedId, code: raw, description: "" };
      } else if (typeof raw === "string" && raw.trim()) {
        unmatched.push({
          raw,
          description: null,
          path: `${pathBase}[${index}]`,
        });
      }
    }
    return narrowed;
  });
}
